import React, { useContext, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import AppContext from '../../../Services/AppContext';
import PersistantData from '../../../Services/PersistantData';
import API_Service, { axiosPutConfig, axiosURIencode } from '../../../Services/API_Service';
import { req_leaves_status } from '../../../Helper/Constants';

const useStyles = makeStyles((theme) => ({
    contentText: {
        marginBottom: theme.spacing(1)
    },
    statusLabel: {
        fontWeight: 600,
        textTransform: 'capitalize'
    },
    reasonField: {
        marginTop: theme.spacing(2)
    }
}));

export default function StatusConfirmDialog({ open, row, newStatus, onClose, onSuccess }) {
    const classes = useStyles();
    const singleton = PersistantData.getInstance();
    const [gState] = useContext(AppContext);
    var { translation: language, client } = gState;
    if (typeof (language) === 'undefined') {
        language = singleton.getlang();
    }
    const { enqueueSnackbar } = useSnackbar();

    const [actionName, setActionName] = useState('');
    const [rejectReason, setRejectReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const formatStatus = (raw) => {
        const t = (language && language[raw]) || raw;
        if (/^[A-Z]+$/.test(t)) {
            return t.charAt(0) + t.slice(1).toLowerCase();
        }
        return t;
    };

    useEffect(() => {
        if (open) {
            setActionName('');
            setRejectReason('');
            setSubmitting(false);
        }
    }, [open]);

    const handleConfirm = () => {
        if (!row || !newStatus) return;

        if ((newStatus === 'approved' || newStatus === 'rejected') && !actionName.trim()) {
            enqueueSnackbar(language.leave_name_required, { variant: 'error' });
            return;
        }

        if (newStatus === 'rejected' && !rejectReason.trim()) {
            enqueueSnackbar(language.reject_reason_required, { variant: 'error' });
            return;
        }

        const payload = { id: row.id, status: newStatus, action_by: actionName.trim() };
        if (newStatus === 'rejected') {
            payload.reject_reason = rejectReason.trim();
        }

        setSubmitting(true);
        API_Service.put(req_leaves_status, axiosURIencode(payload), axiosPutConfig(client))
            .then(({ data }) => {
                if (parseInt(data.result) === 1) {
                    enqueueSnackbar(`${language.leave_status_updated} ${formatStatus(newStatus)}`, {
                        variant: 'success',
                        autoHideDuration: 2000
                    });
                    if (onSuccess) onSuccess();
                } else {
                    enqueueSnackbar(data.message || 'Update failed', { variant: 'error' });
                    setSubmitting(false);
                }
            })
            .catch(() => {
                enqueueSnackbar('Network error', { variant: 'error' });
                setSubmitting(false);
            });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{language.leave_confirm_status}</DialogTitle>
            <DialogContent>
                <Typography className={classes.contentText}>
                    {language.leave_confirm_status}{' '}
                    <span className={classes.statusLabel}>
                        {formatStatus(newStatus)}
                    </span>?
                </Typography>
                {(newStatus === 'approved' || newStatus === 'rejected') && (
                    <TextField
                        className={classes.reasonField}
                        fullWidth
                        variant="outlined"
                        label={newStatus === 'approved' ? language.approved_by : language.rejected_by}
                        value={actionName}
                        onChange={(e) => setActionName(e.target.value)}
                        required
                    />
                )}
                {newStatus === 'rejected' && (
                    <TextField
                        className={classes.reasonField}
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        label={language.reject_reason}
                        placeholder={language.reject_reason_required}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        required
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    {language.cancel}
                </Button>
                <Button onClick={handleConfirm} color="primary" variant="contained" disabled={submitting}>
                    {language.confirm}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
